<template>
  <v-container fluid>

    <!-- HEADER -->
    <div class="d-flex align-center justify-space-between mb-6 calendar-page-header">
      <h2 class="text-h5 font-weight-bold">📆 {{ $t('calendar.title') }}</h2>

      <v-btn
        v-if="canCreateNewYear"
        color="primary"
        dark
        rounded
        data-tour="calendar-add-year"
        @click="addNewYear"
      >
        <v-icon left>mdi-plus</v-icon>
        {{ $t('calendar.addYear') }}
      </v-btn>
    </div>

    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <!-- PANEL DE AÑOS -->
    <v-expansion-panels multiple v-model="expandedPanels">
      <v-expansion-panel
        v-for="yearData in years"
        :key="yearData.year"
        class="year-panel"
      >

        <v-expansion-panel-header class="year-header">
          <span class="year-title">{{ $t('calendar.workYear', { year: yearData.year }) }}</span>
        </v-expansion-panel-header>

        <v-expansion-panel-content>

          <!-- ACCIONES -->
          <div class="d-flex align-center justify-space-between mb-4 year-actions">
            <v-btn color="primary" rounded dark @click="openDialog(yearData)">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('calendar.addHoliday') }}
            </v-btn>

            <!-- EDITAR VACACIONES -->
            <div class="d-flex align-center year-config" style="gap:16px">
              <template v-if="editingYearId === yearData.year">
                <v-form
                  :ref="`yearForm-${yearData.year}`"
                  v-model="yearConfigValid[yearData.year]"
                  class="d-flex align-center year-config-form"
                  style="gap:16px"
                >

                <v-text-field
                  v-model.number="yearData.vacationDays"
                  :label="$t('calendar.vacation')"
                  type="number"
                  dense outlined class="vac-input"
                  :rules="[rules.required]"
                  required
                />

                <v-text-field
                  v-model.number="yearData.personalDays"
                  :label="$t('calendar.personalDays')"
                  type="number"
                  dense outlined class="vac-input"
                  :rules="[rules.required]"
                  required
                />

                <v-btn small color="success" @click="saveYearConfig(yearData)">{{ $t('common.save') }}</v-btn>
                <v-btn small text @click="editingYearId = null">{{ $t('common.cancel') }}</v-btn>
                </v-form>

              </template>

              <template v-else>
                <div class="year-stat">{{ $t('calendar.vacation') }}: <b>{{ yearData.vacationDays }}</b></div>
                <div class="year-stat">{{ $t('calendar.personalDays') }}: <b>{{ yearData.personalDays }}</b></div>
                <v-btn small color="primary" class="year-edit-btn" @click="editingYearId = yearData.year">{{ $t('common.edit') }}</v-btn>
              </template>
            </div>
          </div>

          <!-- ⭐⭐⭐ LEYENDA DE COLORES ANTES DEL CALENDARIO ⭐⭐⭐ -->
          <div class="legend mb-4 d-flex align-center" style="gap:22px;">
            <div class="legend-item d-flex align-center">
              <span class="legend-color nacional"></span> {{ $t('calendar.national') }}
            </div>
            <div class="legend-item d-flex align-center">
              <span class="legend-color autonomico"></span> {{ $t('calendar.regional') }}
            </div>
            <div class="legend-item d-flex align-center">
              <span class="legend-color empresa"></span> {{ $t('calendar.company') }}
            </div>
          </div>

          <!-- CALENDARIO -->
          <v-row dense>
            <v-col
              v-for="month in 12"
              :key="month"
              cols="12" sm="6" md="4" lg="3"
              class="mb-6"
            >
              <v-card outlined class="month-card">

                <v-card-title class="justify-center font-weight-bold month-title">
                  {{ monthName(month) }}
                </v-card-title>

                <v-divider class="mx-4 mb-2"></v-divider>

                <!-- CALENDARIO -->
                <v-sheet class="calendar-sheet">
                  <v-calendar
                    type="month"
                    :start="getMonthStart(yearData.year, month)"
                    :end="getMonthEnd(yearData.year, month)"
                    :short-weekdays="true"
                    :weekdays="[1,2,3,4,5,6,0]"
                    :hide-header="true"
                    class="calendar-month"
                  >

                    <!-- 🎨 SLOT DE DÍA -->
                    <template v-slot:day="slotProps">
                      <div
                        v-bind="slotProps.attributes"
                        :class="[
                          'calendar-day',
                          slotProps.outside ? 'outside' : '',
                          holidayTypeClassFromDate(slotProps.date)
                        ]"
                      >
                        <div class="day-number">{{ slotProps.day }}</div>
                      </div>
                    </template>

                  </v-calendar>
                </v-sheet>

              </v-card>
            </v-col>
          </v-row>

          <!-- LISTA DE FESTIVOS -->
          <h3 class="text-h6 font-weight-bold mt-8 mb-3">
            📋 {{ $t('calendar.holidaysTitle', { year: yearData.year }) }}
          </h3>

          <v-simple-table dense class="holiday-table mb-8" v-if="yearData.holidays.length">
            <thead>
              <tr>
                <th>{{ $t('common.date') }}</th>
                <th>{{ $t('calendar.reason') }}</th>
                <th>{{ $t('common.type') }}</th>
                <th class="text-right">{{ $t('common.actions') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(holiday, index) in yearData.holidays" :key="index">
                <td>{{ formatDateEs(holiday.date) }}</td>
                <td>{{ holiday.name }}</td>
                <td>
                  <v-chip small :color="eventColor(holiday)" dark>{{ holidayTypeText(holiday.type) }}</v-chip>
                </td>
                <td class="text-right">
                  <v-btn icon small @click="openDialog(yearData, holiday, index)">
                    <v-icon small color="primary">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small @click="askDelete(yearData, index)">
                    <v-icon small color="red">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>

          </v-simple-table>

          <div v-else class="text-body-2 grey--text mb-8">
            {{ $t('calendar.emptyHolidays') }}
          </div>

        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- DIALOG NUEVO/EDITAR FESTIVO -->
    <v-dialog v-model="dialog" max-width="760px">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          {{ editingHoliday ? $t('calendar.editHoliday') : $t('calendar.addHoliday') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="holidayForm" v-model="holidayValid">
            <template v-if="editingHoliday">
              <v-text-field
                v-model="newHoliday.name"
                :label="$t('calendar.reason')"
                dense
                outlined
                :rules="[rules.required]"
                required
              />

              <v-select
                v-model="newHoliday.type"
                :items="holidayTypeItems"
                item-text="text"
                item-value="value"
                :label="$t('common.type')"
                dense outlined
                :rules="[rules.required]"
                required
              />

              <v-text-field
                v-model="newHoliday.date"
                :label="$t('common.date')"
                type="date"
                prepend-icon="mdi-calendar"
                dense outlined
                :min="activeYear?.year + '-01-01'"
                :max="activeYear?.year + '-12-31'"
                :rules="[rules.required]"
                required
              />
            </template>

            <template v-else>
              <div
                v-for="(row, index) in holidayRows"
                :key="index"
                class="holiday-row"
              >
                <v-text-field
                  v-model="row.date"
                  :label="$t('common.date')"
                  type="date"
                  prepend-icon="mdi-calendar"
                  dense outlined
                  :min="activeYear?.year + '-01-01'"
                  :max="activeYear?.year + '-12-31'"
                  :rules="[rules.required]"
                  required
                />

                <v-text-field
                  v-model="row.name"
                  :label="$t('calendar.reason')"
                  dense outlined
                  :rules="[rules.required]"
                  required
                />

                <v-select
                  v-model="row.type"
                  :items="holidayTypeItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('common.type')"
                  dense outlined
                  :rules="[rules.required]"
                  required
                />

                <v-btn
                  icon
                  class="holiday-row-delete"
                  :disabled="holidayRows.length === 1"
                  @click="removeHolidayRow(index)"
                >
                  <v-icon color="red">mdi-delete</v-icon>
                </v-btn>
              </div>

              <v-btn small text color="primary" class="mt-2" @click="addHolidayRow">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('calendar.addRow') }}
              </v-btn>
            </template>
          </v-form>

        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" dark @click="saveHoliday">{{ $t('common.save') }}</v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>

    <!-- DIALOG CONFIRMACIÓN ELIMINAR -->
    <v-dialog v-model="confirmDeleteDialog" max-width="380px">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">{{ $t('calendar.deleteTitle') }}</v-card-title>

        <v-card-text>
          {{ $t('calendar.deleteConfirm') }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="confirmDeleteDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="red" dark @click="deleteConfirmed">{{ $t('common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { db } from "@/firebase";
import { collection, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { getUserErrorMessage } from "@/utils/errors";

export default {
  name: "CalendarLaboral",

  computed: {
    companyId() {
      return this.$store.getters["auth/companyId"];
    },

    canCreateNewYear() {
      if (!this.years.length) return true;
      const current = new Date().getFullYear();
      const maxYear = Math.max(...this.years.map(y => y.year));
      return maxYear < current + 1;
    },
    holidayTypeItems() {
      return [
        { text: this.$t("calendar.national"), value: "Nacional" },
        { text: this.$t("calendar.regional"), value: "Autonómico" },
        { text: this.$t("calendar.company"), value: "Empresa" },
      ];
    },
    rules() {
      return {
        required: (value) => value !== null && value !== undefined && value !== "" || this.$t("common.required"),
      };
    },
  },

  data() {
    return {
      years: [],
      expandedPanels: [0],
      dialog: false,
      confirmDeleteDialog: false,
      message: "",
      messageType: "success",
      holidayValid: false,
      yearConfigValid: {},
      deleteTarget: null,
      editingHoliday: false,
      editingYearId: null,
      newHoliday: { name: "", date: "", type: "Nacional" },
      holidayRows: [],
      activeYear: null,
      editIndex: null
    };
  },

  async created() {
    await this.loadYears();
  },

  methods: {
    /* ------------ CARGA AÑOS ------------ */
    async loadYears() {
      try {
        this.message = "";
        const snap = await getDocs(collection(db, "companies", this.companyId, "workCalendars"));
        this.years = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b)=>a.year-b.year);

        if (!this.years.length) {
          await this.createYear(new Date().getFullYear());
          await this.loadYears();
          return;
        }

        this.expandCurrentYear();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "calendar.loadError", this.$i18n), "error");
      }
    },

    expandCurrentYear() {
      const current = new Date().getFullYear();
      const currentIndex = this.years.findIndex(y => Number(y.year) === current);
      this.expandedPanels = [currentIndex >= 0 ? currentIndex : 0];
    },

    async createYear(year) {
      await setDoc(doc(db, "companies", this.companyId, "workCalendars", year.toString()), {
        year,
        vacationDays: 0,
        personalDays: 0,
        holidays: []
      });
    },

    async addNewYear() {
      const current = new Date().getFullYear();
      const max = Math.max(...this.years.map(y => y.year));
      const nextYear = Math.max(current, max) + 1;

      if (!this.canCreateNewYear) return;

      try {
        await this.createYear(nextYear);
        await this.loadYears();
        this.expandedPanels = [this.years.length - 1];
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "calendar.saveError", this.$i18n), "error");
      }
    },

    /* ------------ FESTIVOS ------------ */

    holidayTypeClassFromDate(date) {
      const event = this.years.flatMap(y => y.holidays).find(h => h.date === date);
      if (!event) return "";
      return {
        Nacional: "holiday-nacional",
        Autonómico: "holiday-autonomico",
        Empresa: "holiday-empresa"
      }[event.type];
    },

    eventColor(event) {
      return {
        Nacional: "#d32f2f",
        Autonómico: "#1976d2",
        Empresa: "#7b1fa2",
      }[event.type];
    },

    holidayTypeText(type) {
      return {
        Nacional: this.$t("calendar.national"),
        Autonómico: this.$t("calendar.regional"),
        Empresa: this.$t("calendar.company"),
      }[type] || type;
    },

    openDialog(yearData, holiday = null, index = null) {
      this.activeYear = yearData;
      this.editIndex = index;
      this.newHoliday = holiday ? { ...holiday } : { name: "", date: "", type: "Nacional" };
      this.holidayRows = holiday ? [] : [this.createHolidayRow()];
      this.editingHoliday = !!holiday;
      this.dialog = true;
      this.$nextTick(() => this.$refs.holidayForm?.resetValidation());
    },

    createHolidayRow() {
      return { name: "", date: `${this.activeYear?.year || new Date().getFullYear()}-01-01`, type: "Nacional" };
    },

    addHolidayRow() {
      this.holidayRows.push(this.createHolidayRow());
    },

    removeHolidayRow(index) {
      if (this.holidayRows.length === 1) return;
      this.holidayRows.splice(index, 1);
    },

    async saveHoliday() {
      if (!this.$refs.holidayForm.validate()) return;

      const y = this.activeYear;
      const holidays = [...y.holidays];

      if (this.editIndex !== null) {
        if (!this.newHoliday.date) {
          this.showMessage(this.$t("common.required"), "error");
          return;
        }

        holidays.splice(this.editIndex, 1, this.newHoliday);
      } else {
        const rows = this.holidayRows.map(row => ({
          name: row.name,
          type: row.type,
          date: row.date
        }));

        if (!rows.length || rows.some(row => !row.name || !row.type || !row.date)) {
          this.showMessage(this.$t("calendar.completeRows"), "error");
          return;
        }

        const rowsByDate = new Map(rows.map(row => [row.date, row]));
        holidays.splice(0, holidays.length, ...holidays.filter(h => !rowsByDate.has(h.date)), ...rowsByDate.values());
      }

      holidays.sort((a, b) => a.date.localeCompare(b.date));

      try {
        await updateDoc(doc(db, "companies", this.companyId, "workCalendars", y.year.toString()), { holidays });

        this.dialog = false;
        await this.loadYears();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "calendar.saveError", this.$i18n), "error");
      }
    },

    /* CONFIRM DELETE */
    askDelete(yearData, index) {
      this.deleteTarget = { yearData, index };
      this.confirmDeleteDialog = true;
    },

    async deleteConfirmed() {
      try {
        const { yearData, index } = this.deleteTarget;
        const holidays = yearData.holidays.filter((_, i) => i !== index);

        await updateDoc(doc(db, "companies", this.companyId, "workCalendars", yearData.year.toString()), { holidays });

        this.confirmDeleteDialog = false;
        await this.loadYears();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "calendar.deleteError", this.$i18n), "error");
      }
    },

    /* ------------ EDITAR VACACIONES ------------ */
    async saveYearConfig(yearData) {
      const yearForm = this.$refs[`yearForm-${yearData.year}`];
      const formRef = Array.isArray(yearForm) ? yearForm[0] : yearForm;
      if (formRef && !formRef.validate()) return;

      try {
        await updateDoc(
          doc(db, "companies", this.companyId, "workCalendars", yearData.year.toString()),
          {
            vacationDays: yearData.vacationDays,
            personalDays: yearData.personalDays
          }
        );

        this.editingYearId = null;
        await this.loadYears();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "calendar.saveError", this.$i18n), "error");
      }
    },

    /* ------------ FECHAS ------------ */
    monthName(m) {
      return new Intl.DateTimeFormat(this.$i18n.locale, { month: "long" }).format(new Date(2000, m - 1));
    },

    getMonthStart(y, m) {
      return `${y}-${String(m).padStart(2, "0")}-01`;
    },

    getMonthEnd(y, m) {
      const last = new Date(y, m, 0).getDate();
      return `${y}-${String(m).padStart(2, "0")}-${last}`;
    },

    formatDateEs(d) {
      return new Date(d).toLocaleDateString(this.$i18n.locale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    }
  }
};
</script>

<style scoped>
/* ----------------------
   TÍTULO AÑO LABORAL
----------------------- */
.year-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #3a3551;
}

/* ----------------------
   LEYENDA DE COLORES
----------------------- */
.legend-item {
  font-size: 0.9rem;
  font-weight: 500;
  color: #444;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 6px;
  display: inline-block;
  border: 2px solid transparent;
}

.legend-color.nacional {
  background-color: rgba(255, 80, 80, 0.65);
  border-color: #d32f2f;
}

.legend-color.autonomico {
  background-color: rgba(80, 150, 255, 0.65);
  border-color: #1976d2;
}

.legend-color.empresa {
  background-color: rgba(180, 80, 255, 0.65);
  border-color: #7b1fa2;
}

.holiday-row {
  align-items: start;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(150px, 0.9fr) minmax(180px, 1.4fr) minmax(150px, 0.9fr) 40px;
}

.holiday-row-delete {
  margin-top: 2px;
}

/* ----------------------
   CALENDARIO
----------------------- */
::v-deep .calendar-day {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.day-number {
  font-size: 0.9rem;
  font-weight: 500;
}

::v-deep .calendar-day.outside {
  opacity: 0.25;
}

/* ----------------------
   COLORES FESTIVOS
----------------------- */
::v-deep .holiday-nacional {
  background-color: rgba(255, 80, 80, 0.65) !important;
  border: 2px solid #d32f2f !important;
}

::v-deep .holiday-autonomico {
  background-color: rgba(80, 150, 255, 0.65) !important;
  border: 2px solid #1976d2 !important;
}

::v-deep .holiday-empresa {
  background-color: rgba(180, 80, 255, 0.65) !important;
  border: 2px solid #7b1fa2 !important;
}

@media (max-width: 600px) {
  .calendar-page-header,
  .year-config-form,
  .legend {
    align-items: stretch !important;
    flex-direction: column;
  }

  .calendar-page-header,
  .year-actions {
    gap: 12px;
  }

  .calendar-page-header {
    margin-bottom: 16px !important;
  }

  .calendar-page-header .v-btn {
    width: 100%;
  }

  .calendar-page-header h2 {
    font-size: 1.2rem !important;
    line-height: 1.3;
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 12px 16px;
  }

  .year-header {
    min-height: 52px;
    padding: 12px 14px;
  }

  .year-title {
    font-size: 1.08rem;
    line-height: 1.3;
  }

  .year-actions {
    align-items: stretch !important;
    background: #f8f7fd;
    border: 1px solid rgba(74, 58, 255, 0.08);
    border-radius: 12px;
    flex-direction: column;
    padding: 12px;
  }

  .year-actions > .v-btn {
    width: 100%;
  }

  .year-config {
    align-items: stretch !important;
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 8px !important;
  }

  .year-stat {
    background: #fff;
    border: 1px solid rgba(58, 53, 81, 0.1);
    border-radius: 10px;
    color: #3a3551;
    font-size: 0.88rem;
    padding: 10px 8px;
    text-align: center;
  }

  .year-edit-btn {
    grid-column: 1 / -1;
    justify-self: stretch;
  }

  .year-config,
  .year-config-form,
  .legend {
    gap: 8px !important;
  }

  .year-config-form .v-btn {
    width: 100%;
  }

  .legend {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    display: grid !important;
    grid-template-columns: 1fr;
    padding: 10px 12px;
  }

  .legend-item {
    justify-content: flex-start;
  }

  .vac-input {
    width: 100%;
  }

  .month-card {
    border-radius: 10px;
    overflow: hidden;
  }

  .month-title {
    font-size: 0.95rem;
    padding: 12px 8px;
  }

  .holiday-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }

  .holiday-row {
    gap: 4px;
    grid-template-columns: 1fr 42px;
  }

  .holiday-row .v-input {
    grid-column: 1 / -1;
  }

  .holiday-row-delete {
    grid-column: 2;
    justify-self: end;
    margin-top: -8px;
  }

  ::v-deep .v-dialog {
    margin: 12px;
  }
}

</style>
